/**
 * Get the main image URL for a product or cart item
 * 
 * @param {Object} item - Product or cart item object
 * @returns {string} - URL to the product image or a placeholder
 */
export const getProductImageUrl = (item) => {
  // Default placeholder image
  const placeholderImage = 'https://via.placeholder.com/150';
  
  if (!item) return placeholderImage;
  
  try {
    // If direct image URL exists, use it
    if (typeof item.image === 'string') {
      return item.image;
    }
    
    // Check for direct image object
    if (item.image && item.image.url) {
      return item.image.url;
    }
    
    // Check for coverImage (which we set in cart mapper)
    if (item.coverImage) {
      if (typeof item.coverImage === 'string') {
        return item.coverImage;
      }
      if (item.coverImage.url) {
        return item.coverImage.url;
      }
    }
    
    // If product has photos collection
    if (item.product?.photos && item.product.photos.length > 0) {
      // Get the cover image using coverImageIndex or default to first image
      const coverIndex = item.product.coverImageIndex || 0;
      const photoObj = item.product.photos[coverIndex < item.product.photos.length ? coverIndex : 0];
      
      // Return the URL of the photo object
      if (photoObj && photoObj.url) {
        return photoObj.url;
      }
      
      // For Strapi v3 format - direct URL string
      if (typeof photoObj === 'string') {
        return photoObj;
      }
      
      // For arrays of strings
      if (Array.isArray(item.product.photos) && typeof item.product.photos[0] === 'string') {
        return item.product.photos[0];
      }
    }
    
    // Check for photos directly on the item (not nested in product)
    if (item.photos && item.photos.length > 0) {
      const coverIndex = item.coverImageIndex || 0;
      const photoObj = item.photos[coverIndex < item.photos.length ? coverIndex : 0];
      
      // Return the URL of the photo
      if (photoObj && photoObj.url) {
        return photoObj.url;
      }
      
      // For Strapi v3 format - direct URL
      if (typeof photoObj === 'string') {
        return photoObj;
      }
    }
    
    // Check for legacy image structure
    if (item.product?.image?.url) {
      return item.product.image.url;
    }
    
    // Try for photo URLs from Strapi in formats we might encounter
    if (item.product?.photos?.[0]?.formats?.small?.url) {
      return item.product.photos[0].formats.small.url;
    }
    
    if (item.product?.photos?.[0]?.formats?.thumbnail?.url) {
      return item.product.photos[0].formats.thumbnail.url;
    }

    // Check for images array
    if (item.images && item.images.length > 0) {
      const firstImage = item.images[0];
      if (typeof firstImage === 'string') {
        return firstImage;
      }
      if (firstImage && firstImage.url) {
        return firstImage.url;
      }
    }
    
    // Check if the item itself is a URL string (very rare edge case)
    if (typeof item === 'string' && (item.startsWith('http') || item.startsWith('/'))) {
      return item;
    }
    
    // For SheYou specific format seen in the cart
    if (item.product && item.product.images && item.product.images.length > 0) {
      return item.product.images[0];
    }
    
    // Check product attributes for image
    if (item.attributes && item.attributes.images && item.attributes.images.length > 0) {
      return item.attributes.images[0];
    }
    
    console.log('Debug - Failed to find image for item:', 
      JSON.stringify({
        hasProduct: !!item.product,
        hasImage: !!item.image,
        hasImages: !!(item.images && item.images.length),
        hasPhotos: !!(item.photos && item.photos.length),
        productHasImages: !!(item.product && item.product.images && item.product.images.length),
        itemKeys: Object.keys(item)
      })
    );
    
  } catch (err) {
    console.error('Error getting product image:', err);
  }
  
  // Fallback to placeholder
  return placeholderImage;
};

/**
 * Format product price for display
 * 
 * @param {number|string} price - The product price
 * @param {boolean} includeCurrency - Whether to include the currency symbol
 * @returns {string} - Formatted price
 */
export const formatPrice = (price, includeCurrency = true) => {
  if (price === null || price === undefined) return includeCurrency ? '$0.00' : '0.00';
  
  const numPrice = typeof price === 'string' ? parseFloat(price) : price;
  
  if (isNaN(numPrice)) return includeCurrency ? '$0.00' : '0.00';
  
  return includeCurrency 
    ? `$${numPrice.toFixed(2)}` 
    : numPrice.toFixed(2);
}; 